import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';

type UserItem = { user: string };
type UsersResponse = { users?: UserItem[] };

export default function WebUiMaintenanceUsersPage() {
  const [users, setUsers] = React.useState<string[]>([]);
  const [selectedUser, setSelectedUser] = React.useState('');
  const [addUser, setAddUser] = React.useState('');
  const [addPassword, setAddPassword] = React.useState('');
  const [addPasswordRepeat, setAddPasswordRepeat] = React.useState('');
  const [editPw1, setEditPw1] = React.useState('');
  const [editPw2, setEditPw2] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const reloadUsers = React.useCallback(async () => {
    const data = await postWebUiApi<UsersResponse>({
      service: 'user',
      access: 'get',
      command: 'get users',
    });
    const userNames = (data.users ?? []).map((item) => item.user);
    setUsers(userNames);
    if (userNames.length > 0) {
      setSelectedUser((prev) =>
        userNames.includes(prev) ? prev : userNames[0],
      );
    } else {
      setSelectedUser('');
    }
  }, []);

  React.useEffect(() => {
    let alive = true;
    reloadUsers()
      .then(() => alive && setError(null))
      .catch((err) => alive && setError((err as Error).message));
    return () => {
      alive = false;
    };
  }, [reloadUsers]);

  const runAction = React.useCallback(
    async (
      request: {
        service: string;
        access: 'set';
        command: string;
        parameters: unknown;
      },
      ok: string,
    ) => {
      try {
        setLoading(true);
        await postWebUiApi(request);
        await reloadUsers();
        setMessage(ok);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [reloadUsers],
  );

  return (
    <WebUiPageWrapper titleList={['Maintenance', 'Users']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Add User</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Username</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    value={addUser}
                    onChange={(e) => setAddUser(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>New Password</td>
                <td>
                  <WebUiInput
                    type="password"
                    inputSize="sm"
                    value={addPassword}
                    onChange={(e) => setAddPassword(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>New Password (Repeat)</td>
                <td>
                  <WebUiInput
                    type="password"
                    inputSize="sm"
                    value={addPasswordRepeat}
                    onChange={(e) => setAddPasswordRepeat(e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton
              disabled={loading || !addUser.trim()}
              onClick={() => {
                if (addPassword !== addPasswordRepeat) {
                  setError("Password doesn't match.");
                  return;
                }
                runAction(
                  {
                    service: 'user',
                    access: 'set',
                    command: 'add user',
                    parameters: {
                      user: addUser,
                      password: addPassword,
                      level: 0,
                    },
                  },
                  'User created.',
                );
              }}
            >
              Add User
            </WebUiButton>
          </div>
        </div>

        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">Edit User</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Setting</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>Username</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    options={users.map((u) => ({ label: u, value: u }))}
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>New Password</td>
                <td>
                  <WebUiInput
                    type="password"
                    inputSize="sm"
                    value={editPw1}
                    onChange={(e) => setEditPw1(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>New Password (Repeat)</td>
                <td>
                  <WebUiInput
                    type="password"
                    inputSize="sm"
                    value={editPw2}
                    onChange={(e) => setEditPw2(e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="flex gap-2">
            <WebUiButton
              disabled={loading || !selectedUser}
              onClick={() => {
                if (editPw1 !== editPw2) {
                  setError("Password doesn't match.");
                  return;
                }
                runAction(
                  {
                    service: 'user',
                    access: 'set',
                    command: 'change user password',
                    parameters: { user: selectedUser, password: editPw1 },
                  },
                  'Password changed.',
                );
              }}
            >
              Change Password
            </WebUiButton>
            <WebUiButton
              disabled={loading || !selectedUser}
              onClick={() =>
                runAction(
                  {
                    service: 'user',
                    access: 'set',
                    command: 'remove user',
                    parameters: { user: selectedUser },
                  },
                  'User removed.',
                )
              }
            >
              Remove User
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
